import type { ISwiper, IProductGetResponse, IProductsGetTopProductsResponse } from '../../definitions'

import { String } from 'stdlib/String'
import { Array }  from 'stdlib/Array'
import { URL }    from 'stdlib/URL'
import { is }     from 'stdlib/is'

import DOM from '@common/DOM/DOM'

import ajax from '../../ajax'

import renderProducts     from './renderProducts'
import updateStyleContent from './updateStyleContent'

const PRODUCT_STYLE_ID = 'product-style'

/**
 * Termék oldal tartalmának legenerálása.
 * @param sku - Termék cikkszáma.
 */
export default async function renderProduct (sku: string): Promise<void> {
  const productSection = document.body.querySelector('.product-section')

  // Nem található a rész, ahol a termék adatai megjelenne.
  if (!productSection) {
    return
  }

  await DOM.createScript('https://cdn.jsdelivr.net/npm/swiper@11/swiper-element-bundle.min.js')

  const url = URL.createRoute(ajax.routes.product.GetProduct, [ sku ])

  const { isSuccess, result } = await ajax.send(url)

  // Jelzés a felhasználónak, hogy hiba volt a lekérdezés alatt.
  if (!isSuccess) {
    const template = document.body.querySelector<HTMLTemplateElement>(
      'template#product-not-found-product-template'
    )

    if (!template) {
      return
    }

    productSection.innerHTML = template.innerHTML
  }

  // Kapcsolódó/top termékeknél megjelenő adatok
  let products = isSuccess
    ? (result as IProductGetResponse).product.relatedProducts
    : []

  const needTopProductsTitle = is.empty(products)

  // Nincs kapcsolódó termék, vagy nem volt sikeres a termék lekérdezés.
  // Ebben az esetben top termékeket kell megjeleníteni.
  if (is.empty(products)) {
    const topProductResponse = await ajax.send(ajax.routes.product.TopProducts)

    if (topProductResponse.isSuccess) {
      products = (topProductResponse.result as IProductsGetTopProductsResponse).topProducts
    }
  }

  const relatedProductsTitle = document.body.querySelector('#product h2.products-section__title')

  if (relatedProductsTitle) {
    relatedProductsTitle.textContent = needTopProductsTitle
      ? 'Top termékek'
      : 'Kapcsolódó termékek'
  }

  // Kapcsolódó termékek
  await renderProducts('#product .products-section', products)

  if (!isSuccess) {
    return
  }

  const { product } = result as IProductGetResponse

  // oxlint-disable-next-line require-atomic-updates
  document.title = product.name

  const template = document.body.querySelector<HTMLTemplateElement>(
    'template#product-product-template'
  )

  if (!template) {
    return
  }

  productSection.innerHTML = template.innerHTML

  // Large images
  const largeImages = productSection.querySelector('.product-section__large-images')

  if (largeImages) {
    const largeImagesfragment = document.createDocumentFragment()

    // Large videó
    if (product.youtubeLink) {
      const videoElem = document.createElement('imm-video')

      videoElem.classList.add('product-section__large-image')
      videoElem.setAttribute('youtube-id', product.youtubeLink)
      videoElem.setAttribute('data-index', '0')

      largeImagesfragment.appendChild(videoElem)
    }

    product.images.forEach((image, i) => {
      const img = document.createElement('img')

      img.classList.add('product-section__large-image')

      img.src = `https://wsrv.nl?output=webp&url=${ image }`
      img.alt = `${ product.name } termék kép ${ i + 1 }.`
      img.setAttribute('data-index', `${ i + 1 }`)

      largeImagesfragment.appendChild(img)
    })

    largeImages.innerHTML = ''

    largeImages.appendChild(largeImagesfragment)
  }

  // Small images
  const smallImages = productSection.querySelector<ISwiper>('.product-section__small-images')

  const smallImageTemplate = productSection.querySelector<HTMLTemplateElement>('template#product-small-image-template')

  if (!smallImageTemplate) {
    return
  }

  if (smallImages) {
    const fragment = document.createDocumentFragment()

    const smallVideoImageTemplate = productSection.querySelector<HTMLTemplateElement>(
      'template#product-small-video-image-template'
    )

    // Video small image
    if (product.youtubeLink && smallVideoImageTemplate) {
      const slide = document.createElement('swiper-slide')

      slide.classList.add('product-section__small-image')

      slide.innerHTML = smallVideoImageTemplate.innerHTML

      const input = slide.querySelector<HTMLInputElement>('.product-section__small-image-input')

      if (input) {
        input.id = 'product-image-0'
        input.checked = true
      }

      const label = slide.querySelector<HTMLLabelElement>('.product-section__small-image-label')

      if (label) {
        label.htmlFor = 'product-image-0'
      }

      const img = slide.querySelector<HTMLImageElement>('.product-section__small-img')

      if (img) {
        img.src = `https://wsrv.nl?output=webp&url=https://img.youtube.com/vi/${  product.youtubeLink  }/hqdefault.jpg`
        img.alt = `${ product.name } termék videó`
      }

      fragment.appendChild(slide)
    }

    // Small image
    product.images.forEach((image, i) => {
      const slide = document.createElement('swiper-slide')

      slide.classList.add('product-section__small-image')

      slide.innerHTML = smallImageTemplate.innerHTML

      const input = slide.querySelector<HTMLInputElement>('.product-section__small-image-input')

      if (input) {
        input.id = `product-image-${ i + 1 }`
        input.checked = i === 0 && !product.youtubeLink
      }

      const label = slide.querySelector<HTMLLabelElement>('.product-section__small-image-label')

      if (label) {
        label.htmlFor = `product-image-${ i + 1 }`
      }

      const img = slide.querySelector<HTMLImageElement>('.product-section__small-img')

      if (img) {
        img.src = `https://wsrv.nl?output=webp&url=${ image }`
        img.alt = `${ product.name } termék kép ${ i + 1 }.`
      }

      fragment.appendChild(slide)
    })

    smallImages.appendChild(fragment)

    smallImages.initialize()
  }

  // Termék név
  const title = productSection.querySelector('.product-section__title')

  if (title) {
    title.textContent = product.name
  }

  // Termék cikkszám
  const skuElem = productSection.querySelector('.product-section__sku')

  if (skuElem) {
    skuElem.textContent = product.sku
  }

  // Termék leírása
  const description = productSection.querySelector('.product-section__description')

  if (description) {
    description.innerHTML = product.description
  }

  const categories = productSection.querySelector('.product-section__categories')

  // Temrék kateróriái
  if (categories) {
    categories.innerHTML = ''

    const fragment = document.createDocumentFragment()

    const categoryTemplate = productSection.querySelector<HTMLTemplateElement>('template#product-category-template')

    if (!categoryTemplate) {
      return
    }

    product.categories.forEach((category) => {
      const categoryElem = document.createElement('div')

      categoryElem.classList.add('product-section__category')

      categoryElem.innerHTML = categoryTemplate.innerHTML

      const categoryName = categoryElem.querySelector('.product-section__category-name')

      if (categoryName) {
        categoryName.textContent = category
      }

      fragment.appendChild(categoryElem)
    })

    categories.appendChild(fragment)
  }

  // Termék bruttó ára
  const grossPrice = productSection.querySelector('p.price__gross-price')

  if (grossPrice) {
    grossPrice.textContent = String.formatCurrency(product.grossPrice)
  }

  // Termék nettó ára
  const netPrice = productSection.querySelector('p.price__net-price')

  if (netPrice) {
    netPrice.textContent = `(${ String.formatCurrency(product.netPrice) } + ÁFA)`
  }

  // Vásárlás gomb
  const priceLink = productSection.querySelector<HTMLAnchorElement>('a.product-section__price-link')

  if (priceLink) {
    priceLink.href = product.link

     // Előrendelhető vagy vásárolható a termék
    priceLink.textContent = product.stock === 0
      ? 'Előrendelhető'
      : 'Vásárlás'
  }

  const content = `
    ${ Array.times(product.images.length + 1, (i) => {
      const selector = [
        '.product-section__large-images:has(~ .product-section__small-images',
        `.product-section__small-image-input[id="product-image-${ i }"]:checked)`,
        `.product-section__large-image[data-index="${ i }"]`
      ].join(' ')

      return `
        ${ selector } {
          display: block;
        }
      `
    }).join('\n') }
  `

  updateStyleContent(PRODUCT_STYLE_ID, content)
}
